import { useEffect, useState } from 'react';

import { ApiError } from '@/api/client';
import { characters, type CharacterRowDTO } from '@/api/endpoints';
import { useSession } from '@/store/session';
import { GearIcon, gearKeyForCharacter } from '@/components/ui/GearIcon';
import { Icon } from '@/components/ui/Icon';

/** The 6-character roster. One Explorer + one Companion active at a time. */
export function Heroes() {
  const [roster, setRoster] = useState<CharacterRowDTO[] | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const applyWallet = useSession((s) => s.applyWallet);
  const refresh = useSession((s) => s.refresh);

  const load = () => void characters.list().then((r) => setRoster(r.roster));
  useEffect(load, []);

  /* activate() returns no wallet; unlock()/upgrade() do. One signature covers
     all three rather than three near-identical handlers. */
  async function act(key: string, fn: () => Promise<unknown>) {
    setBusy(key); setError(null);
    try {
      const res = (await fn()) as { wallet?: Parameters<typeof applyWallet>[0] };
      if (res && res.wallet) applyWallet(res.wallet);
      load();
      void refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'That did not work.');
    } finally {
      setBusy(null);
    }
  }

  if (!roster) return <p className="py-6 text-center text-[11px] text-ink-dim">Loading…</p>;

  const active = roster.filter((c) => c.active);

  return (
    <div className="h-full overflow-y-auto px-3 pb-6 pt-3.5">
      <h2 className="font-pixel text-[9px] text-torch-core">ACTIVE PARTY</h2>
      <p className="mt-2 text-[11px] leading-relaxed text-ink-dim">
        One Explorer and one Companion at a time. Bonuses stack with your axe, lamp, material and
        depth — but no character can turn a trash rock into Shwe Kyat.
      </p>
      <div className="mt-2.5 grid grid-cols-2 gap-1.5">
        {(['explorer', 'companion'] as const).map((slot) => {
          const c = active.find((x) => x.slot === slot);
          return (
            <div key={slot} className="flex items-baseline justify-between gap-1.5 rounded-lg
                                       bg-black/30 px-2.5 py-2 text-[11px]">
              <span className="capitalize text-ink-dim">{slot}</span>
              <b className="text-ink-hi">{c ? c.name.split(' ')[0] : '—'}</b>
            </div>
          );
        })}
      </div>

      {error && <p role="alert" className="mt-2 text-xs text-[var(--danger)]">{error}</p>}

      <div className="mt-3 space-y-3">
        {roster.map((c) => (
          <article key={c.key}
            className="rounded-md border bg-[var(--surface)] p-3.5"
            style={{ borderColor: c.active ? c.accent : 'var(--hairline)' }}>
            <header className="flex items-start gap-3">
              {/* Each hero's own render. All six shared one glyph before, which
                  made a roster screen that could not tell you who was who. */}
              <span className="grid h-13 w-13 shrink-0 place-items-center rounded-xl border
                               border-ui-lit bg-[linear-gradient(#3B2917,#1D1309)] p-1">
                <GearIcon gearKey={gearKeyForCharacter(c.key)}
                          fallback={<Icon name="hero" size={26} />} size={44} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="font-extrabold" style={{ color: c.accent }}>{c.name}</p>
                <p className="font-pixel text-[7px] uppercase text-ink-dim">
                  {c.title} · {c.role}
                </p>
                <p className="text-coin" aria-label={`${c.stars} of 5 stars`}>
                  {'★'.repeat(Math.floor(Number(c.stars)))}
                  {Number(c.stars) % 1 ? '⯪' : ''}
                </p>
              </div>
              {c.active && (
                <span className="rounded-md border px-2 py-1 font-pixel text-[7px]"
                      style={{ color: c.accent, borderColor: c.accent }}>ACTIVE</span>
              )}
            </header>

            <div className="mt-2.5 grid grid-cols-2 gap-1.5 text-[11px]">
              <div className="flex justify-between rounded-lg bg-black/30 px-2.5 py-2">
                <span className="text-ink-dim">Bonus</span>
                <b style={{ color: c.accent }}>+{c.bonus.effective}%</b>
              </div>
              <div className="flex justify-between rounded-lg bg-black/30 px-2.5 py-2">
                <span className="text-ink-dim">Level</span>
                <b className="text-ink-hi">{c.unlocked ? `${c.level} / ${c.max_level}` : 'locked'}</b>
              </div>
            </div>

            <p className="mt-2 text-[11px] text-ink-dim">
              <b className="text-torch-core">{c.special.key.replace(/_/g, ' ')}</b> — {c.special.description}
            </p>

            <div className="mt-2.5 flex flex-wrap gap-1.5">
              {c.unlocked ? (
                <>
                  {!c.active && (
                    <Btn onClick={() => void act(c.key, () => characters.activate(c.key))}
                         busy={busy === c.key}>Set active</Btn>
                  )}
                  {c.next_level?.cost_coins != null ? (
                    <Btn primary busy={busy === c.key}
                         onClick={() => void act(c.key, () => characters.upgrade(c.key))}>
                      <IconLabel icon="coin">
                        Level up · {c.next_level.cost_coins.toLocaleString('en-US')}
                      </IconLabel>
                    </Btn>
                  ) : <Btn disabled>Max level</Btn>}
                </>
              ) : c.unlock.coins != null ? (
                <Btn primary busy={busy === c.key}
                     onClick={() => void act(c.key, () => characters.unlock(c.key, 'coins'))}>
                  <IconLabel icon="coin">
                    Unlock · {c.unlock.coins.toLocaleString('en-US')}
                  </IconLabel>
                </Btn>
              ) : (
                <Btn busy={busy === c.key}
                     onClick={() => void act(c.key, () => characters.unlock(c.key, 'kyat'))}>
                  <IconLabel icon="kyat">Unlock · {c.unlock.kyat}</IconLabel>
                </Btn>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function IconLabel({ icon, children }: { icon: 'coin' | 'kyat'; children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1">
      {children}
      <Icon name={icon} size={13} className="shrink-0" />
    </span>
  );
}

function Btn({ children, onClick, primary, disabled, busy }: {
  children: React.ReactNode; onClick?: () => void;
  primary?: boolean; disabled?: boolean; busy?: boolean;
}) {
  return (
    <button type="button" onClick={onClick} disabled={disabled || busy}
      className={`rounded-sm border px-3 py-2 text-xs font-bold active:translate-y-0.5
        disabled:opacity-40 ${primary
          ? 'border-[rgba(255,220,170,.5)] bg-[linear-gradient(#FFA945,#E0761C)] text-[#2A1503]'
          : 'border-ui-lit bg-[linear-gradient(#3A2718,var(--cave-floor))]'}`}>
      {busy ? '…' : children}
    </button>
  );
}
